// Utilitários de formatação de data — timezone-safe.
//
// Por que existe: o backend retorna DATEs do Postgres como ISO timestamps
// UTC (ex.: "2026-05-12T00:00:00.000Z"). Quando o browser está em BRT
// (UTC-3) e a gente faz `new Date(s).toLocaleDateString()`, o resultado
// é UM DIA A MENOS — porque 00:00 UTC = 21:00 do dia anterior em BRT.
//
// Esse bug afetou várias telas (heatmap, lista de atividades, alocações).
// O fix: NUNCA passar string ISO direto pro `new Date()` de display.
// Em vez disso, fatia os 10 primeiros chars (yyyy-mm-dd) e formata manual,
// ou força a conversão pra meio-dia local pra estar longe da fronteira de
// timezone.

/**
 * Converte string de data (ISO completo OU "yyyy-mm-dd" puro) pra
 * "dd/mm/yyyy" em pt-BR sem timezone shift.
 *
 * @example
 * fmtDataBr('2026-05-12T00:00:00.000Z')  // "12/05/2026" (não "11/05/2026")
 * fmtDataBr('2026-05-12')                 // "12/05/2026"
 * fmtDataBr(null)                          // "—"
 * fmtDataBr('')                            // "—"
 */
export function fmtDataBr(s: string | null | undefined): string {
  if (!s) return '—';
  // Pega só a parte da data (yyyy-mm-dd), descarta hora/timezone
  const dataPart = String(s).slice(0, 10);
  const m = dataPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(s);  // formato inesperado — devolve original
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/**
 * Momento (TIMESTAMPTZ, ex. `criado_em`) → "dd/mm/yyyy" no dia de São Paulo,
 * o mesmo dia que o servidor usa para ordenar. `fmtDataBr` fatia o texto UTC
 * e, para um pedido aberto às 22h, mostraria o dia seguinte. DATE sem hora
 * ("yyyy-mm-dd") continua com `fmtDataBr`.
 *
 * @example
 * fmtMomentoBr('2026-09-24T01:00:00.000Z')  // "23/09/2026"
 * fmtMomentoBr('2026-09-24')                // "24/09/2026"
 */
export function fmtMomentoBr(v: string | null | undefined): string {
  if (!v) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(v))) return fmtDataBr(v);
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

// L17 (2026-06-03) — helpers pra input type="datetime-local"
// datetime-local NAO suporta timezone — lê/escreve no LOCAL do browser.
// Backend retorna ISO UTC. Convertemos antes de mostrar e ao salvar.
//
// Pegadinha: `.slice(0, 16)` de ISO UTC ("2026-07-01T00:00:00.000Z")
// pega "2026-07-01T00:00" mas o input interpreta como LOCAL — em BRT
// mostraria 00:00 quando deveria ser 21:00 do dia anterior.

/**
 * Converte ISO UTC pra valor de <input type="datetime-local"> em hora local.
 *
 * @example
 * isoToDatetimeLocal('2026-07-01T00:00:00.000Z')  // "2026-06-30T21:00" em BRT
 */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Converte valor de <input type="datetime-local"> (hora local) pra ISO UTC.
 *
 * @example
 * datetimeLocalToIso('2026-06-30T21:00')  // "2026-07-01T00:00:00.000Z" em BRT
 */
export function datetimeLocalToIso(local: string | null | undefined): string {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

/**
 * Converte string ISO pra Date em meio-dia LOCAL (longe da fronteira UTC).
 * Use quando precisar comparar/calcular datas, NÃO quando precisar exibir.
 *
 * @example
 * dataAoMeioDia('2026-05-12T00:00:00.000Z').getDate()  // 12 sempre
 */
/**
 * DATE do Postgres → "yyyy-mm-dd" puro, para alimentar `dayjs()` de um
 * DatePicker sem perder um dia.
 *
 * 🚨 `dayjs("2027-05-10T00:00:00.000Z")` em BRT vira 09/05 às 21h, e o
 *    `format('YYYY-MM-DD')` do submit grava 2027-05-09. Ou seja: a data anda
 *    UM DIA PARA TRÁS a cada vez que alguém salva o formulário — inclusive num
 *    save que nem tocou no campo. Já `dayjs("2027-05-10")` é meia-noite LOCAL
 *    e o ida-e-volta fecha.
 *    É a mesma pegadinha do cabeçalho deste arquivo, na porta de ENTRADA do
 *    formulário em vez da de exibição. Este helper mora aqui, e não numa cópia
 *    por tela, porque o projeto já tem três casos de régua copiada que divergiu.
 *
 * @example dataIso('2027-05-10T00:00:00.000Z')  // "2027-05-10"
 */
export function dataIso(s: string | null | undefined): string | null {
  if (!s) return null;
  const dataPart = String(s).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(dataPart) ? dataPart : null;
}

export function dataAoMeioDia(s: string | null | undefined): Date | null {
  if (!s) return null;
  const dataPart = String(s).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dataPart)) return null;
  return new Date(dataPart + 'T12:00:00');
}

/**
 * Formata "yyyy-mm-dd" → "dd/mm" (curto).
 */
export function fmtDataCurta(s: string | null | undefined): string {
  if (!s) return '—';
  const dataPart = String(s).slice(0, 10);
  const m = dataPart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(s);
  return `${m[3]}/${m[2]}`;
}

/**
 * Hoje em formato yyyy-mm-dd (LOCAL, não UTC). Útil pra defaults de input
 * type=date.
 */
export function hojeIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}

/**
 * Soma N dias a uma data ISO yyyy-mm-dd, devolve outra ISO yyyy-mm-dd.
 * Timezone-safe.
 */
export function somarDiasIso(s: string | null | undefined, dias: number): string | null {
  const d = dataAoMeioDia(s);
  if (!d) return null;
  d.setDate(d.getDate() + dias);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dia}`;
}
